import {
  AddUpdateTaskParams,
  AttachmentFile,
  EventByDay,
  Frequency,
  Task,
  toDateString,
} from '@nextforce/api'
import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { isGifType, isImageType } from 'file_folder_repository/util'
import { cloneDeep, isEmpty, isUndefined } from 'lodash'
import moment from 'moment'
import { ColorValue, Platform } from 'react-native'
import ImageCropPicker from 'shared/wrappers/react-native-image-crop-picker'

export function getDefaultEndDateForFrequency(
  frequency: Frequency | undefined,
  startDate: string,
  interval: number = 1
): string {
  const start = moment(startDate)

  switch (frequency) {
    case 'DAILY':
      // 1 week from start (or more based on interval)
      return toDateString(start.clone().add(Math.max(7, interval * 7), 'days'))
    case 'WEEKLY':
      // 1 month from start (or more based on interval)
      return toDateString(start.clone().add(Math.max(4, interval * 4), 'weeks'))
    case 'MONTHLY':
      // 1 year from start (or more based on interval)
      return toDateString(start.clone().add(Math.max(12, interval * 12), 'months'))
    case 'YEARLY':
      // 10 years from start (or more based on interval)
      return toDateString(start.clone().add(Math.max(10, interval * 10), 'years'))
    default:
      // Default fallback: 1 month from start
      return toDateString(start.clone().add(1, 'month'))
  }
}

// Function to get the appropriate plural form for a unit based on interval
export const getPluralForm = (
  unitType: 'repeatDay' | 'repeatWeek' | 'repeatMonth' | 'repeatYear',
  count: number
) => {
  return t(`${unitType}`, { count })
}

export function getRepeatText(
  taskFields: AddUpdateTaskParams
): string | undefined {
  const { frequency, interval, byDay, monthlyOn, until, dueDate } = taskFields

  if (!frequency) return undefined

  // Default interval to 1 if not specified
  const intervalValue = interval || 1

  // Format the until date if present
  const untilText = until ? ` ${t('repeatUntil')} ${toDateString(until)}` : ''
  switch (frequency) {
    case 'DAILY':
      return `${t('repeating')} ${t('every')} ${
        intervalValue === 1 ? '' : intervalValue + ' '
      }${getPluralForm('repeatDay', intervalValue)}${untilText}`

    case 'WEEKLY':
      let daysText = ''
      if (byDay && byDay.length > 0) {
        // Convert byDay codes to readable day names using translations
        const daysList = byDay.map(day => t(`repeatDayNames.${day}`)).join(', ')
        daysText = ` ${t('repeatOn')} ${daysList}`
      }
      return `${t('repeating')} ${t('every')} ${
        intervalValue === 1 ? '' : intervalValue + ' '
      }${getPluralForm('repeatWeek', intervalValue)}${daysText}${untilText}`

    case 'MONTHLY':
      let dayText = ''
      if (monthlyOn) {
        dayText = ` ${t('repeatOnDay')} ${monthlyOn}`
      }
      return `${t('repeating')} ${t('every')} ${
        intervalValue === 1 ? '' : intervalValue + ' '
      }${getPluralForm('repeatMonth', intervalValue)}${dayText}${untilText}`

    case 'YEARLY':
      let monthDayText = ''

      if (dueDate) {
        monthDayText = ` ${t('repeatOn')} ${moment(dueDate).format('MMMM Do')}`
      }

      return `${t('repeating')} ${t('every')} ${
        intervalValue === 1 ? '' : intervalValue + ' '
      }${getPluralForm('repeatYear', intervalValue)}${monthDayText}${untilText}`

    default:
      return undefined
  }
}

export function validateRecurringTask(
  taskFields: AddUpdateTaskParams
): boolean {
  if (!taskFields.frequency || !taskFields.interval || !taskFields.until) {
    return false
  }
  switch (taskFields.frequency) {
    case 'DAILY':
      return true
    case 'WEEKLY':
      return !isEmpty(taskFields.byDay)
    case 'MONTHLY':
      return !isUndefined(taskFields.monthlyOn)
    case 'YEARLY':
      return true
    default:
      return false
  }
}

export function sortByDays(byDays: EventByDay[]): EventByDay[] {
  // Define day order constants for sorting
  const dayOrder: Record<EventByDay, number> = {
    SU: 0,
    MO: 1,
    TU: 2,
    WE: 3,
    TH: 4,
    FR: 5,
    SA: 6,
  }

  return [...byDays].sort((a, b) => dayOrder[a] - dayOrder[b])
}

export type TaskStatus = 'done' | 'due' | 'open' | 'late'

export function getTaskStatus(task: Task | undefined): TaskStatus {
  if (!task) {
    return 'due'
  }
  const isUnassigned = !task.assignedTo
  const dueDate = task.occurrences.find(
    occurrence => !occurrence.deleted
  )?.dueDate

  const isPastDue = dueDate ? moment(dueDate).isBefore(moment()) : false

  if (task.occurrences.some(occurrence => occurrence.completed)) {
    return 'done'
  }

  if (isPastDue) {
    return 'late'
  }

  if (isUnassigned) {
    return 'open'
  }
  return 'due'
}

export function useTaskStatusColor(taskStatus: TaskStatus): ColorValue {
  const { theme } = useTheme()
  switch (taskStatus) {
    case 'done':
      return theme.colors.teal
    case 'due':
      return theme.colors.yellow
    case 'open':
      return theme.colors.grey12
    case 'late':
      return theme.colors.peach
  }
}

export const editPhotoAttachment = async ({
  photo,
  selectedPhotos,
  setSelectedPhotos,
}: {
  photo: AttachmentFile
  selectedPhotos: AttachmentFile[]
  setSelectedPhotos: React.Dispatch<React.SetStateAction<AttachmentFile[]>>
}) => {
  const attachmentIndex = selectedPhotos.findIndex(
    selectedAttachment => selectedAttachment.uri === photo.uri
  )
  if (attachmentIndex !== -1) {
    const attachment = selectedPhotos[attachmentIndex]
    if (isImageType(attachment.mimeType) && !isGifType(attachment.mimeType)) {
      const editedImage = await ImageCropPicker.openCropper({
        mediaType: 'photo',
        path: attachment.uri,
        freeStyleCropEnabled: true,
        ...(Platform.OS === 'ios'
          ? { width: attachment.width, height: attachment.height }
          : {}),
      }).catch(e => null)

      if (editedImage) {
        const editedAttachment = cloneDeep(attachment)
        editedAttachment.height = editedImage.height
        editedAttachment.width = editedImage.width
        editedAttachment.uri = editedImage.path

        const newAttachments = [...selectedPhotos]
        newAttachments[attachmentIndex] = editedAttachment

        setSelectedPhotos(newAttachments)
      }
    }
  }
}
